import { visibleWorkPackages, withheldWorkPackageCount } from '@/demo/scoping'
import { gateIsClientFacingFor, gateStatusLabelFor } from '@/modules/architecture'
import {
  WORK_PACKAGES,
  projectById,
  roleByKey,
  type Project,
  type RoleKey,
  type WorkPackage,
  type WorkPackageStatus,
} from '@/demo/data'

/**
 * Content for `/my/board` (consultant, multi-project) and `/my/project`
 * (client employee, one project). Every string and every figure on those two
 * surfaces comes from here or from the seed; the page contains no literal.
 */

export const BOARD_CHROME = {
  multi: { moduleLabel: 'MY WORK', breadcrumb: 'My board' },
  single: { moduleLabel: 'MY PROJECT' },
} as const

export const COLUMN_LABELS: Readonly<Record<WorkPackageStatus, string>> = {
  backlog: 'Backlog',
  'in-progress': 'In progress',
  'in-review': 'In review',
  blocked: 'Blocked',
  done: 'Done',
}

export const BOARD_LABELS = {
  emptyColumn: 'No work packages in this column',
  needsPerson: 'Needs a person',
  internal: 'internal',
  boardAriaLabel: 'Work packages by status',
  filterLabel: 'Filter',
  allProjects: 'All projects',
  clearFilter: 'Clear filter',
  workPackages: 'Work packages',
  readOnly: 'Read-only',
  gateLabel: 'Gate',
} as const

export const MULTI_HEADER = {
  title: 'My board',
  lede: 'Every work package assigned to you, across the engagements you are staffed on. The board is read-only this round — a card opens its work package on the project’s build board.',
  kpiAssigned: 'Assigned to me',
  kpiBlocked: 'Blocked',
  kpiNextDate: 'Next date',
  glanceAriaLabel: 'My work at a glance',
  urlNote: 'Choosing an engagement writes ?project= to the URL, so a filtered board is shareable; a clear filter control returns the unfiltered board.',
  noneBlocked: 'Nothing blocked',
  noneDue: 'No dated work package',
} as const

export const SINGLE_HEADER = {
  lede: 'Your project with Perpleks: what the team is working on, what is waiting on your side, and where the current phase gate stands.',
  phaseAriaLabel: 'Phase',
} as const

/** ISO day → "15 Sep". Fixed inputs only; no clock is read. */
export const shortDay = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

export interface BoardFilterChip {
  projectId: string
  label: string
  count: number
}

export const filterChipsFor = (cards: readonly WorkPackage[]): readonly BoardFilterChip[] => {
  const ids = [...new Set(cards.map((c) => c.projectId))]
  return ids.map((projectId) => ({
    projectId,
    label: projectById(projectId).client,
    count: cards.filter((c) => c.projectId === projectId).length,
  }))
}

/** The consultant's own packages: their scope, narrowed to their person. */
export const myWorkPackages = (role: RoleKey): readonly WorkPackage[] => {
  const personId = roleByKey(role).personId
  if (!personId) return []
  return visibleWorkPackages(role).filter((w) => w.assigneeId === personId)
}

export interface BoardKpis {
  assigned: number
  engagements: readonly string[]
  blocked: readonly WorkPackage[]
  next: WorkPackage | null
}

export const boardKpis = (cards: readonly WorkPackage[]): BoardKpis => {
  const dated = cards.filter((c) => c.dueDate !== null)
  const next = [...dated].sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))[0] ?? null
  return {
    assigned: cards.length,
    engagements: [...new Set(cards.map((c) => projectById(c.projectId).client))],
    blocked: cards.filter((c) => c.status === 'blocked'),
    next,
  }
}

export const multiDensityNote =
  'Minimalist: a card carries its title, engagement, hours and the date that binds it — nothing else. Rate, cost and margin live on the engagement’s budget surface and are linked from the card rather than restated here. Blockers are never withheld: the blocked column stays open at every density.'

export const ENGAGEMENT_PHASE_COUNT = 7

/**
 * Project-keyed views of the gate vocabulary, which lives in
 * `@/demo/architecture` — these are its phase-keyed helpers, projected.
 */
export const gateStatusLabel = (project: Project): string => gateStatusLabelFor(project.phase)

export const gateStatusIsClientFacing = (project: Project): boolean =>
  gateIsClientFacingFor(project.phase)

export interface SingleBoardCopy {
  sharedBand: string
  densityNote: string
  withheld: number
  total: number
  shared: number
}

/**
 * The client view states what it withholds, WITH THE COUNT — a visible
 * affordance, never a silent gap (AC-2.4). The Backlog column is empty by
 * construction here because all three of van Dijk's Backlog rows are internal;
 * the column still renders, and the note says why.
 */
export const singleBoardCopy = (role: RoleKey, projectId: string): SingleBoardCopy => {
  const shared = visibleWorkPackages(role, projectId).length
  const withheld = withheldWorkPackageCount(role, projectId)
  const total = WORK_PACKAGES.filter((w) => w.projectId === projectId).length
  const withheldInBacklog = WORK_PACKAGES.filter(
    (w) => w.projectId === projectId && !w.clientVisible && w.status === 'backlog'
  ).length
  return {
    shared,
    withheld,
    total,
    sharedBand: `${BOARD_LABELS.readOnly} · ${shared} of ${total} work packages are shared with your team`,
    densityNote: `Client view. ${withheldInBacklog} work packages in Backlog are internal delivery planning and are not shared — the column stays open and says so rather than disappearing. ${withheld} of ${total} are internal in total. Rates, cost, margin, utilisation and internal notes are withheld on every client surface. Nothing that blocks your project is ever withheld.`,
  }
}
